import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Chip,
  Button,
  Collapse,
  LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './AITipCard.css';

const AITipCard = ({ tips, onClose, position = 'bottom-right' }) => {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const expanded = true; // Always keep expanded for better UX

  const currentTip = tips[currentTipIndex];
  const progress = ((currentTipIndex + 1) / tips.length) * 100;

  const handleNext = () => {
    if (currentTipIndex < tips.length - 1) {
      setCurrentTipIndex(currentTipIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentTipIndex > 0) {
      setCurrentTipIndex(currentTipIndex - 1);
    }
  };

  const handleComplete = () => {
    onClose();
  };

  const getPositionStyles = () => {
    const positions = {
      'bottom-right': { bottom: '80px', right: '20px' },
      'bottom-left': { bottom: '80px', left: '20px' },
      'top-right': { top: '80px', right: '20px' },
      'top-left': { top: '80px', left: '20px' },
    };
    return positions[position] || positions['bottom-right'];
  };

  if (!tips || tips.length === 0) return null;

  return (
    <Card
      className="ai-tip-card"
      sx={{
        position: 'fixed',
        ...getPositionStyles(),
        width: '380px',
        maxWidth: '90vw',
        zIndex: 9999,
        boxShadow: '0 8px 32px rgba(0, 34, 68, 0.3)',
        borderRadius: '16px',
        border: '2px solid #002244',
        animation: 'slideIn 0.3s ease-out',
        '@keyframes slideIn': {
          from: {
            opacity: 0,
            transform: 'translateY(20px)'
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #002244 0%, #0D3B66 100%)',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LightbulbIcon sx={{ fontSize: '24px', color: '#FFD700' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            Welcome Guide
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: '4px',
          backgroundColor: '#E0E0E0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#FFD700'
          }
        }}
      />

      <Collapse in={expanded}>
        <CardContent sx={{ padding: '20px' }}>
          {/* Step Counter */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Chip
              label={`Step ${currentTipIndex + 1} of ${tips.length}`}
              size="small"
              sx={{
                backgroundColor: '#E8F4F8',
                color: '#002244',
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
            {currentTip.category && (
              <Chip
                label={currentTip.category}
                size="small"
                sx={{
                  backgroundColor: '#FFF3CD',
                  color: '#856404',
                  fontSize: '0.75rem'
                }}
              />
            )}
          </Box>

          {/* Tip Title */}
          <Typography
            variant="h6"
            sx={{
              color: '#002244',
              fontWeight: 600,
              fontSize: '1.1rem',
              mb: 1.5,
              lineHeight: 1.3
            }}
          >
            {currentTip.title}
          </Typography>

          {/* Tip Description */}
          <Typography
            variant="body2"
            sx={{
              color: '#333',
              fontSize: '0.9rem',
              lineHeight: 1.6,
              mb: 2
            }}
          >
            {currentTip.description}
          </Typography>

          {/* Action Items */}
          {currentTip.actionItems && currentTip.actionItems.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#002244',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  mb: 1
                }}
              >
                Quick Actions:
              </Typography>
              {currentTip.actionItems.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 0.8
                  }}
                >
                  <CheckCircleIcon
                    sx={{ fontSize: '16px', color: '#28a745', mt: 0.3 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '0.85rem', color: '#555', flex: 1 }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Navigation Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 3
            }}
          >
            <Button
              onClick={handlePrevious}
              disabled={currentTipIndex === 0}
              startIcon={<NavigateBeforeIcon />}
              sx={{
                color: '#002244',
                textTransform: 'none',
                fontWeight: 600,
                '&:disabled': { color: '#ccc' }
              }}
            >
              Previous
            </Button>

            {currentTipIndex < tips.length - 1 ? (
              <Button
                onClick={handleNext}
                variant="contained"
                endIcon={<NavigateNextIcon />}
                sx={{
                  backgroundColor: '#002244',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#0D3B66' }
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                variant="contained"
                endIcon={<CheckCircleIcon />}
                sx={{
                  backgroundColor: '#28a745',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#218838' }
                }}
              >
                Got it!
              </Button>
            )}
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default AITipCard;
